/**
 * useControlGridData — shared data loader for the PathKit Control Grid.
 *
 * Both the standalone /internal/control-grid page and the System Admin tab
 * call this hook so neither has to duplicate the JSON imports, the fetch
 * for the gitignored last-run.json, or the builder invocation.
 *
 * Why fetch (not import.meta.glob) for tests/last-run.json:
 *   `import.meta.glob({ eager: true })` inlines the JSON at module-load time
 *   and Vite's file watcher does not reliably HMR-invalidate when the gitignored
 *   tests/last-run.json is rewritten by `npm run test:control-grid`. That made
 *   the dashboard stick on stale data until the dev server itself was restarted.
 *   Fetching the file at render time (with a cache-busting query) sidesteps
 *   Vite's module cache entirely — every browser refresh (or `reload()` call)
 *   pulls the latest JSON the test runner wrote.
 *
 * Pure data — no JSX, no styling. Returns the BuildResult plus `noRun` and
 * `loading` flags so callers can render their own empty / loading states.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  buildControlGrid,
  type BuildResult,
  type CapabilitiesFile,
  type OverridesFile,
  type VitestRun,
} from './build';

import capabilitiesJson from '../../internal/control-grid/capabilities.json';
import overridesJson from '../../internal/control-grid/manual-overrides.json';

const CAPABILITIES = capabilitiesJson as unknown as CapabilitiesFile;
const OVERRIDES = overridesJson as unknown as OverridesFile;

const LAST_RUN_URL = '/tests/last-run.json';

export interface RunTestsResult {
  ok: boolean;
  exitCode: number;
  durationMs: number;
  stderr?: string;
  /** Set when the dev middleware is unreachable, typically because this is
   *  not a local `vite dev` build (production deploys do not include the
   *  POST /__dev/control-grid/run handler at all). */
  unavailable?: boolean;
}

export interface UseControlGridDataResult {
  result: BuildResult;
  /** True when there is no last-run.json (file missing or 404). */
  noRun: boolean;
  /** True during the very first fetch — distinguishes "still loading" from
   *  "actually no run". UIs typically suppress the empty-state banner while
   *  loading is true to avoid flicker. */
  loading: boolean;
  /** True while a UI-triggered vitest run is in flight. */
  running: boolean;
  /** Outcome of the most recent runTests() call, or null if none yet. */
  runStatus: RunTestsResult | null;
  /** Re-fetch tests/last-run.json without a page reload. Used by the
   *  "Refresh" button so the operator can re-run vitest in a terminal and
   *  see the new results without restarting the dev server. */
  reload: () => void;
  /** Trigger `npm run test:control-grid` via the dev-only middleware
   *  (POST /__dev/control-grid/run) and reload() the data on completion.
   *  Resolves to the run outcome. In production builds the middleware does
   *  not exist; the call resolves with `unavailable: true` and the UI
   *  should fall back to "run it in the CLI". */
  runTests: () => Promise<RunTestsResult>;
  /** The capabilities config the builder used — exposed so UI affordances
   *  (e.g. the manual-check dialog) can read manualChecks per row. */
  capabilities: CapabilitiesFile;
  /** The overrides file the builder used — exposed so the manual-check
   *  dialog can merge the operator's new entry into the existing set. */
  overrides: OverridesFile;
}

async function fetchLastRun(): Promise<VitestRun | null> {
  try {
    // Cache-bust on every fetch so neither the browser nor any intermediate
    // proxy can serve a stale copy after the test runner rewrote the file.
    const res = await fetch(`${LAST_RUN_URL}?t=${Date.now()}`, {
      cache: 'no-store',
    });
    if (!res.ok) return null;
    return (await res.json()) as VitestRun;
  } catch {
    return null;
  }
}

export function useControlGridData(): UseControlGridDataResult {
  const [lastRun, setLastRun] = useState<VitestRun | null>(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runStatus, setRunStatus] = useState<RunTestsResult | null>(null);
  // Bumped after each successful fetch so the builder re-runs against the
  // freshly-fetched JSON and downstream useMemos invalidate cleanly.
  const [tick, setTick] = useState(0);

  const reload = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    fetchLastRun().then((data) => {
      if (cancelled) return;
      setLastRun(data);
      setLoading(false);
      setTick((t) => t + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const cancel = reload();
    return cancel;
  }, [reload]);

  const runTests = useCallback(async (): Promise<RunTestsResult> => {
    setRunning(true);
    let outcome: RunTestsResult;
    try {
      const res = await fetch('/__dev/control-grid/run', { method: 'POST' });
      if (res.status === 404) {
        // Production build: the dev middleware was never registered. UI
        // should surface a helpful message and the operator falls back to
        // the CLI workflow.
        outcome = {
          ok: false,
          exitCode: -1,
          durationMs: 0,
          unavailable: true,
        };
      } else if (res.status === 409) {
        outcome = {
          ok: false,
          exitCode: -1,
          durationMs: 0,
          stderr: 'A test run is already in progress.',
        };
      } else {
        outcome = (await res.json()) as RunTestsResult;
      }
    } catch (err) {
      outcome = {
        ok: false,
        exitCode: -1,
        durationMs: 0,
        stderr: err instanceof Error ? err.message : String(err),
      };
    } finally {
      setRunning(false);
    }
    setRunStatus(outcome);
    // Re-fetch last-run.json regardless of exit code: vitest writes the
    // JSON report even when assertions fail, and those failures are
    // exactly what the Control Grid is built to surface.
    if (!outcome.unavailable) reload();
    return outcome;
  }, [reload]);

  const result = useMemo(
    () =>
      buildControlGrid(CAPABILITIES, OVERRIDES, lastRun, {
        now: Date.now(),
      }),
    // `tick` participates so the builder re-runs after each reload even when
    // the JSON identity happened to be referentially equal.
    [lastRun, tick],
  );

  return {
    result,
    noRun: !loading && lastRun === null,
    loading,
    running,
    runStatus,
    reload,
    runTests,
    capabilities: CAPABILITIES,
    overrides: OVERRIDES,
  };
}
